import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Heart, Shield, Zap, Plus, Trash2, Sparkles, Backpack,
  ScrollText, Award, Activity, Save, Check, ChevronUp, ChevronDown, Pencil, Skull, Star,
  Swords, BookOpen, PawPrint, ShieldCheck, LayoutGrid, X, Moon, Coffee, Dices,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PageLoader, Modal, Field, TextInput, Btn, ConfirmButton, ExpandableText } from "@/components/common";
import { SpellPickerModal } from "@/components/SpellPicker";
import {
  ABILITIES, SKILLS, mod, fmtMod, RARITY_COLORS, profBonusForLevel,
  POINT_BUY_BUDGET, pointBuyTotal, isCaster, spellSlots, maxHpForLevel,
} from "@/lib/dnd";

const SECTIONS = [
  { key: "stats", label: "Stats & Skills", icon: Activity },
  { key: "actions", label: "Actions", icon: Swords },
  { key: "inventory", label: "Inventory", icon: Backpack },
  { key: "spells", label: "Spells", icon: Sparkles },
  { key: "features", label: "Features & Traits", icon: Award },
  { key: "proficiencies", label: "Proficiencies", icon: ShieldCheck },
  { key: "background", label: "Background", icon: BookOpen },
  { key: "creatures", label: "Creatures", icon: PawPrint },
  { key: "notes", label: "Notes", icon: ScrollText },
];

const CONDITIONS = ["Blinded", "Charmed", "Deafened", "Frightened", "Grappled", "Incapacitated",
  "Invisible", "Paralyzed", "Petrified", "Poisoned", "Prone", "Restrained", "Stunned", "Unconscious", "Exhaustion"];

// When a character's subclass changes, swap out any features granted by a
// previous subclass of the same class and grant the new subclass's features up
// to the character's current level. Returns the rebuilt features array.
function rebuildSubclassFeatures(char, newSubclass, reference) {
  const classSubs = reference?.subclasses?.[char.class] || {};
  const subNames = Object.keys(classSubs);
  // Drop features that came from any subclass of this class.
  const kept = (char.features || []).filter((f) => !subNames.includes(f.source));
  const level = char.level || 1;
  const added = [];
  const featuresByLevel = classSubs?.[newSubclass]?.features || {};
  Object.entries(featuresByLevel).forEach(([lvl, list]) => {
    if (Number(lvl) <= level) {
      (list || []).forEach((f) => added.push({ id: crypto.randomUUID(), name: f.name, description: f.description, source: newSubclass }));
    }
  });
  return [...kept, ...added];
}


export default function CharacterSheet() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [char, setChar] = useState(null);
  const [tab, setTab] = useState("stats");
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [spellPickerOpen, setSpellPickerOpen] = useState(false);
  const saveTimer = useRef(null);

  const { data, isLoading } = useQuery({
    queryKey: ["character", id],
    queryFn: async () => (await api.get(`/characters/${id}`)).data,
  });
  const { data: reference } = useQuery({ queryKey: ["reference"], queryFn: async () => (await api.get("/meta/reference")).data });

  useEffect(() => { if (data) setChar(data); }, [data]);

  const persist = (next) => {
    setChar(next);
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      setSaving(true);
      try {
        await api.put(`/characters/${id}`, next);
        qc.invalidateQueries({ queryKey: ["characters"] });
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1500);
      } catch { toast.error("Failed to save changes"); }
      finally { setSaving(false); }
    }, 600);
  };
  const set = (patch) => persist({ ...char, ...patch });

  if (isLoading || !char) return <PageLoader />;
  const profBonus = char.proficiency_bonus || profBonusForLevel(char.level);
  const caster = isCaster(char.class);

  const levelUp = () => {
    const newLevel = Math.min(20, (char.level || 1) + 1);
    if (newLevel === char.level) return toast.info("Already at max level (20)");
    const klass = (reference?.classes || []).find((c) => c.name === char.class);
    const conMod = mod(char.abilities?.constitution);
    const hitDie = klass?.hit_die || 8;
    const classFeatures = (klass?.features?.[String(newLevel)] || []).map((f) => ({
      id: crypto.randomUUID(), name: f.name, description: f.description, source: char.class,
    }));
    const subFeatures = (reference?.subclasses?.[char.class]?.[char.subclass]?.features?.[String(newLevel)] || []).map((f) => ({
      id: crypto.randomUUID(), name: f.name, description: f.description, source: char.subclass,
    }));
    const newFeatures = [...classFeatures, ...subFeatures];
    // Deterministic max HP so it mirrors exactly when leveling back down.
    const oldMax = char.hp?.max || maxHpForLevel(hitDie, char.level, conMod);
    const newMax = maxHpForLevel(hitDie, newLevel, conMod);
    const hpGain = Math.max(0, newMax - oldMax);
    set({
      level: newLevel,
      proficiency_bonus: profBonusForLevel(newLevel),
      hit_dice: `${newLevel}d${hitDie}`,
      hp: { ...char.hp, max: newMax, current: (char.hp?.current || 0) + hpGain },
      features: [...(char.features || []), ...newFeatures],
    });
    toast.success(`Leveled up to ${newLevel}! +${hpGain} HP${newFeatures.length ? `, ${newFeatures.length} new feature(s)` : ""}`);
    if (isCaster(char.class)) setTimeout(() => setSpellPickerOpen(true), 400);
  };

  const currentSection = SECTIONS.find((s) => s.key === tab) || SECTIONS[0];
  const CurrentIcon = currentSection.icon;

  return (
    <div className="animate-float-up pb-8">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button onClick={() => navigate("/characters")} className="p-2 rounded-xl glass glass-hover" data-testid="back-btn">
          <ArrowLeft size={18} />
        </button>
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-violet-600 to-violet-900 flex items-center justify-center font-display text-xl font-bold shrink-0">
          {char.name?.[0]?.toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display text-xl sm:text-2xl font-bold text-white truncate">{char.name}</div>
          <p className="text-xs sm:text-sm text-zinc-500 truncate">Lvl {char.level} · {char.race} · {char.class}{char.background ? ` · ${char.background}` : ""}</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => set({ inspiration: !char.inspiration })} title="Inspiration" data-testid="inspiration-toggle"
            className={`p-2.5 rounded-xl border transition ${char.inspiration ? "bg-amber-500/20 border-amber-400/50 text-amber-300" : "glass text-zinc-500"}`}>
            <Star size={16} fill={char.inspiration ? "currentColor" : "none"} />
          </button>
          <Btn variant="ghost" onClick={() => setEditOpen(true)} data-testid="edit-character-btn"><Pencil size={15} /> <span className="hidden sm:inline">Edit</span></Btn>
          <Btn onClick={levelUp} data-testid="level-up-btn"><ChevronUp size={16} /> <span className="hidden sm:inline">Level Up</span></Btn>
        </div>
        <div className="w-full sm:w-auto text-xs text-zinc-500 flex items-center gap-1.5 sm:ml-2">
          {saving ? <><Save size={14} className="animate-pulse" /> Saving…</> :
           savedFlash ? <span className="text-teal-400 flex items-center gap-1.5"><Check size={14} /> Saved</span> :
           <span className="text-zinc-600">Auto-save on</span>}
        </div>
      </div>

      {/* Vitals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-5">
        <HPCard char={char} set={set} />
        <div className="lg:col-span-2 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <VitalEdit label="Armor Class" icon={Shield} value={char.armor_class} color="text-sky-400" onChange={(v) => set({ armor_class: v })} />
          <VitalEdit label="Initiative" icon={Zap} value={char.initiative_bonus} prefix color="text-amber-400" onChange={(v) => set({ initiative_bonus: v })} />
          <VitalEdit label="Speed" icon={Activity} value={char.speed} suffix="ft" color="text-teal-400" onChange={(v) => set({ speed: v })} />
          <div className="glass rounded-2xl p-3 sm:p-4 flex flex-col justify-center min-h-[92px]">
            <div className="flex items-center gap-1.5 mb-1 text-violet-300"><Award size={14} /><span className="text-[10px] sm:text-xs uppercase tracking-wide text-zinc-500">Prof</span></div>
            <div className="font-display text-xl sm:text-2xl font-bold text-violet-300">{fmtMod(profBonus)}</div>
          </div>
        </div>
      </div>

      {/* Conditions */}
      <div className="glass rounded-2xl p-4 mb-5">
        <div className="text-xs uppercase tracking-wide text-zinc-500 mb-2.5 font-medium">Conditions</div>
        <div className="flex flex-wrap gap-2">
          {CONDITIONS.map((cond) => {
            const active = char.conditions?.includes(cond);
            return (
              <button key={cond} data-testid={`cond-${cond}`}
                onClick={() => set({ conditions: active ? char.conditions.filter((x) => x !== cond) : [...(char.conditions || []), cond] })}
                className={`text-xs px-3 py-1.5 rounded-full border transition ${active ? "bg-rose-500/20 border-rose-500/50 text-rose-200" : "border-white/10 text-zinc-500 hover:text-zinc-300 hover:border-white/20"}`}>
                {cond}
              </button>
            );
          })}
        </div>
      </div>

      {/* Section navigator trigger */}
      <div className="mb-5">
        <button
          data-testid="open-section-nav"
          onClick={() => setNavOpen(true)}
          className="w-full sm:w-auto sm:min-w-[280px] flex items-center justify-between gap-4 glass glass-hover rounded-2xl px-4 py-3"
        >
          <span className="flex items-center gap-2.5 text-white font-semibold text-sm">
            <CurrentIcon size={18} className="text-violet-300" /> {currentSection.label}
          </span>
          <span className="flex items-center gap-1.5 text-zinc-500"><LayoutGrid size={15} /><ChevronDown size={15} /></span>
        </button>
      </div>

      {tab === "stats" && <StatsTab char={char} set={set} profBonus={profBonus} />}
      {tab === "actions" && <ActionsTab char={char} set={set} />}
      {tab === "inventory" && <InventoryTab char={char} set={set} />}
      {tab === "spells" && <SpellsTab char={char} set={set} caster={caster} onChoose={() => setSpellPickerOpen(true)} />}
      {tab === "features" && <FeaturesTab char={char} set={set} />}
      {tab === "proficiencies" && <ProficienciesTab char={char} set={set} reference={reference} />}
      {tab === "background" && <BackgroundTab char={char} set={set} reference={reference} />}
      {tab === "creatures" && <CreaturesTab char={char} set={set} />}
      {tab === "notes" && (
        <textarea data-testid="notes-area" value={char.notes || ""} onChange={(e) => set({ notes: e.target.value })}
          placeholder="Backstory, session notes, allies, secrets…"
          className="input-dark w-full rounded-2xl p-5 text-sm min-h-[320px] leading-relaxed" />
      )}

      <SectionNav open={navOpen} onClose={() => setNavOpen(false)} current={tab} onPick={setTab} onEdit={() => setEditOpen(true)} />
      <SpellPickerModal open={spellPickerOpen} onClose={() => setSpellPickerOpen(false)} character={char} onConfirm={(spells) => set({ spells })} />
      <EditModal open={editOpen} onClose={() => setEditOpen(false)} char={char} set={set} reference={reference} />
    </div>
  );
}

function SectionNav({ open, onClose, current, onPick, onEdit }) {
  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="section-nav">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose} className="fixed inset-0 bg-black/75" />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 12 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            className="relative w-full max-w-md rounded-3xl border border-white/10 bg-[#0d0d13] p-5 shadow-2xl"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-white">Jump to section</h3>
              <button onClick={onClose} className="p-1.5 rounded-lg text-zinc-400 hover:text-white hover:bg-white/5" data-testid="section-nav-close"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              {SECTIONS.map((s) => (
                <button key={s.key} data-testid={`nav-section-${s.key}`} onClick={() => { onPick(s.key); onClose(); }}
                  className={`flex items-center gap-2.5 p-3 rounded-2xl border text-left transition ${current === s.key ? "border-violet-500/60 bg-violet-500/10 text-white" : "border-white/10 text-zinc-300 hover:border-white/25"}`}>
                  <s.icon size={18} className={current === s.key ? "text-violet-300" : "text-zinc-500"} />
                  <span className="text-sm font-medium">{s.label}</span>
                </button>
              ))}
              <button data-testid="nav-section-edit" onClick={() => { onEdit(); onClose(); }}
                className="col-span-2 flex items-center gap-2.5 p-3 rounded-2xl border border-white/10 text-zinc-300 hover:border-white/25 transition">
                <Pencil size={18} className="text-zinc-500" /><span className="text-sm font-medium">Edit Character</span>
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

function HPCard({ char, set }) {
  const [amt, setAmt] = useState("");
  const hp = char.hp || { current: 0, max: 0, temp: 0 };
  const ds = char.death_saves || { successes: 0, failures: 0 };
  const pct = Math.max(0, Math.min(100, (hp.current / (hp.max || 1)) * 100));
  const dying = hp.current <= 0;

  const change = (delta) => {
    let cur = hp.current, temp = hp.temp || 0;
    if (delta < 0) { const ft = Math.min(temp, -delta); temp -= ft; cur = Math.max(0, cur - (-delta - ft)); }
    else cur = Math.min(hp.max, cur + delta);
    const patch = { hp: { ...hp, current: cur, temp } };
    if (cur > 0 && (ds.successes || ds.failures)) patch.death_saves = { successes: 0, failures: 0 };
    set(patch);
  };
  const applyField = (sign) => { const v = parseInt(amt) || 0; if (v) change(sign * v); setAmt(""); };
  const setDeath = (kind, n) => {
    const cur = ds[kind] === n ? n - 1 : n;
    set({ death_saves: { ...ds, [kind]: Math.max(0, cur) } });
  };
  const longRest = () => {
    set({
      hp: { ...hp, current: hp.max, temp: 0 },
      death_saves: { successes: 0, failures: 0 },
      spell_slots_used: {},
      actions: (char.actions || []).map((a) => ({ ...a, used: 0 })),
    });
    toast.success("Long rest complete — HP, spell slots & uses restored");
  };
  const shortRest = () => {
    const patch = { actions: (char.actions || []).map((a) => ({ ...a, used: 0 })) };
    if (char.class === "Warlock") patch.spell_slots_used = {};
    set(patch);
    toast.success("Short rest complete");
  };

  return (
    <div className="glass rounded-2xl p-4" data-testid="hp-card">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1.5 text-rose-400"><Heart size={15} /><span className="text-xs uppercase tracking-wide text-zinc-500">Hit Points</span></div>
        <div className="flex items-center gap-1.5">
          <button onClick={() => change(-1)} data-testid="hp-minus" className="w-7 h-7 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 flex items-center justify-center font-bold">−1</button>
          <button onClick={() => change(1)} data-testid="hp-plus" className="w-7 h-7 rounded-lg bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 flex items-center justify-center font-bold">+1</button>
        </div>
      </div>
      <div className="font-display text-2xl font-bold text-white">
        {hp.current}<span className="text-zinc-600 text-lg">/{hp.max}</span>
        {hp.temp > 0 && <span className="text-teal-400 text-sm ml-2">+{hp.temp}</span>}
      </div>
      <div className="h-2 rounded-full bg-white/10 overflow-hidden my-2">
        <div className="h-full bg-gradient-to-r from-rose-600 to-rose-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      {dying ? (
        <div className="mt-2" data-testid="death-saves">
          <div className="flex items-center gap-1.5 text-xs text-rose-300 mb-1.5"><Skull size={13} /> Death Saving Throws</div>
          <div className="flex items-center justify-between text-xs">
            <div className="flex items-center gap-1">
              <span className="text-teal-400 w-12">Success</span>
              {[1, 2, 3].map((n) => (
                <button key={n} onClick={() => setDeath("successes", n)} data-testid={`death-success-${n}`}
                  className={`w-4 h-4 rounded-full border ${ds.successes >= n ? "bg-teal-400 border-teal-300" : "border-zinc-600"}`} />
              ))}
            </div>
            <div className="flex items-center gap-1">
              {[1, 2, 3].map((n) => (
                <button key={n} onClick={() => setDeath("failures", n)} data-testid={`death-fail-${n}`}
                  className={`w-4 h-4 rounded-full border ${ds.failures >= n ? "bg-rose-500 border-rose-400" : "border-zinc-600"}`} />
              ))}
              <span className="text-rose-400 w-12 text-right">Fail</span>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex gap-1.5 mt-1">
          <button onClick={() => applyField(-1)} data-testid="hp-damage" className="px-2.5 py-1.5 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 text-xs font-medium">Damage</button>
          <input value={amt} onChange={(e) => setAmt(e.target.value)} type="number" placeholder="0" className="input-dark rounded-lg w-full text-center text-sm py-1" data-testid="hp-input" />
          <button onClick={() => applyField(1)} data-testid="hp-heal" className="px-2.5 py-1.5 rounded-lg bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 text-xs font-medium">Heal</button>
        </div>
      )}
      <div className="flex gap-1.5 mt-2 pt-2 border-t border-white/[0.06]">
        <button onClick={longRest} data-testid="long-rest-btn" className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-indigo-500/15 text-indigo-300 hover:bg-indigo-500/25 text-xs font-medium"><Moon size={13} /> Long Rest</button>
        <button onClick={shortRest} data-testid="short-rest-btn" className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 text-xs font-medium"><Coffee size={13} /> Short Rest</button>
      </div>
    </div>
  );
}

function VitalEdit({ label, icon: Icon, value, onChange, prefix, suffix, color }) {
  return (
    <div className="glass rounded-2xl p-3 sm:p-4 flex flex-col justify-center min-h-[92px]">
      <div className={`flex items-center gap-1.5 mb-1 ${color}`}><Icon size={14} /><span className="text-[10px] sm:text-xs uppercase tracking-wide text-zinc-500">{label}</span></div>
      <div className="flex items-baseline gap-1">
        {prefix && <span className="text-zinc-400 font-display text-xl sm:text-2xl font-bold">{value >= 0 ? "+" : ""}</span>}
        <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))}
          className="bg-transparent font-display text-xl sm:text-2xl font-bold text-white outline-none w-full min-w-0" />
        {suffix && <span className="text-sm text-zinc-500 shrink-0">{suffix}</span>}
      </div>
    </div>
  );
}

function StatsTab({ char, set, profBonus }) {
  const ab = char.abilities || {};
  const toggleSave = (key) => set({ proficient_saves: char.proficient_saves?.includes(key) ? char.proficient_saves.filter((x) => x !== key) : [...(char.proficient_saves || []), key] });
  const toggleSkill = (key) => set({ proficient_skills: char.proficient_skills?.includes(key) ? char.proficient_skills.filter((x) => x !== key) : [...(char.proficient_skills || []), key] });
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="space-y-3">
        <SectionTitle>Ability Scores</SectionTitle>
        {ABILITIES.map((a) => (
          <div key={a.key} className="glass rounded-2xl p-3 flex items-center gap-3" data-testid={`ability-${a.key}`}>
            <div className="w-12 text-center">
              <div className="text-xs text-zinc-500 font-medium">{a.label}</div>
              <div className="font-display text-lg font-bold text-violet-300">{fmtMod(mod(ab[a.key]))}</div>
            </div>
            <input type="number" value={ab[a.key] ?? 10} onChange={(e) => set({ abilities: { ...ab, [a.key]: Number(e.target.value) } })}
              className="input-dark rounded-xl w-full text-center font-display text-xl font-bold py-2" />
          </div>
        ))}
      </div>
      <div>
        <SectionTitle>Saving Throws</SectionTitle>
        <div className="glass rounded-2xl p-2 space-y-0.5">
          {ABILITIES.map((a) => {
            const prof = char.proficient_saves?.includes(a.key);
            const total = mod(ab[a.key]) + (prof ? profBonus : 0);
            return (
              <button key={a.key} onClick={() => toggleSave(a.key)} data-testid={`save-${a.key}`} className="w-full flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/[0.04] transition">
                <span className={`w-3 h-3 rounded-full border ${prof ? "bg-violet-500 border-violet-400" : "border-zinc-600"}`} />
                <span className="flex-1 text-left text-sm capitalize text-zinc-300">{a.key}</span>
                <span className="font-display font-bold text-white">{fmtMod(total)}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <SectionTitle>Skills</SectionTitle>
        <div className="glass rounded-2xl p-2 space-y-0.5 max-h-[520px] overflow-y-auto">
          {Object.entries(SKILLS).map(([skill, abil]) => {
            const prof = char.proficient_skills?.includes(skill);
            const total = mod(ab[abil]) + (prof ? profBonus : 0);
            return (
              <button key={skill} onClick={() => toggleSkill(skill)} data-testid={`skill-${skill}`} className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl hover:bg-white/[0.04] transition">
                <span className={`w-3 h-3 rounded-full border shrink-0 ${prof ? "bg-violet-500 border-violet-400" : "border-zinc-600"}`} />
                <span className="flex-1 text-left text-sm text-zinc-300">{skill}</span>
                <span className="text-[10px] text-zinc-600 uppercase">{abil.slice(0, 3)}</span>
                <span className="font-display font-bold text-white w-8 text-right">{fmtMod(total)}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function ActionsTab({ char, set }) {
  const [open, setOpen] = useState(false);
  const blank = { name: "", kind: "Attack", range: "", tohit: "", damage: "", notes: "", uses: 0 };
  const [form, setForm] = useState(blank);
  const actions = char.actions || [];
  const weapons = (char.inventory || []).filter((it) => (it.type || "").toLowerCase().includes("weapon"));
  const spells = char.spells || [];

  const add = () => {
    if (!form.name.trim()) return toast.error("Action name required");
    set({ actions: [...actions, { ...form, used: 0, id: crypto.randomUUID() }] });
    setForm(blank); setOpen(false);
  };
  const remove = (id) => set({ actions: actions.filter((a) => a.id !== id) });
  const toggleUse = (id, i) => set({ actions: actions.map((a) => a.id === id ? { ...a, used: i < (a.used || 0) ? i : i + 1 } : a) });

  return (
    <div className="space-y-6">
      <div>
        <SectionTitle>Weapon Attacks</SectionTitle>
        {weapons.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {weapons.map((w) => (
              <div key={w.id} className="glass rounded-2xl p-4" data-testid={`action-weapon-${w.id}`}>
                <div className="flex items-center gap-2"><Swords size={15} className="text-rose-400" /><span className="font-semibold text-white">{w.name}</span></div>
                {w.description && <ExpandableText text={w.description} lines={3} className="text-xs text-zinc-500 mt-1 leading-relaxed" />}
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-5 text-center">No weapons in your inventory yet.</p>}
      </div>

      <div>
        <SectionTitle>Spell Actions</SectionTitle>
        {spells.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {[...spells].sort((a, b) => a.level - b.level).map((sp) => (
              <div key={sp.id} className="glass rounded-2xl p-4" data-testid={`action-spell-${sp.id}`}>
                <div className="flex items-center gap-2"><Sparkles size={15} className="text-violet-300" /><span className="font-semibold text-white">{sp.name}</span></div>
                <div className="text-xs text-zinc-500 mt-0.5">{sp.level === 0 ? "Cantrip" : `Level ${sp.level}`} · {sp.school}</div>
              </div>
            ))}
          </div>
        ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-5 text-center">No spells known.</p>}
      </div>

      <div>
        <div className="flex justify-between items-center mb-3">
          <SectionTitle>Custom Actions</SectionTitle>
          <Btn data-testid="add-action-btn" onClick={() => setOpen(true)}><Plus size={16} /> Add Action</Btn>
        </div>
        {actions.length ? (
          <div className="grid sm:grid-cols-2 gap-3">
            {actions.map((a) => {
              const dmgRoll = a.damage && /\d*d\d+/i.test(a.damage) ? a.damage.replace(/\s+/g, "") : null;
              const hitRaw = (a.tohit || "").trim();
              const hitRoll = /^[+-]?\d+$/.test(hitRaw) ? `1d20${hitRaw.startsWith("-") ? hitRaw : "+" + hitRaw.replace("+", "")}` : null;
              return (
                <div key={a.id} className="glass rounded-2xl p-4 flex items-start gap-3" data-testid={`custom-action-${a.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-white">{a.name}</span>
                      <span className="text-[10px] uppercase bg-white/[0.06] text-zinc-400 px-1.5 py-0.5 rounded">{a.kind}</span>
                    </div>
                    <div className="text-xs text-zinc-500 mt-1 space-x-2">
                      {a.range && <span>Range: <span className="text-zinc-300">{a.range}</span></span>}
                      {a.tohit && <span>To-hit/DC: <span className="text-zinc-300">{a.tohit}</span></span>}
                    </div>
                    {(hitRoll || dmgRoll) && (
                      <div className="flex flex-wrap items-center gap-2 mt-2">
                        {hitRoll && <RollChip notation={hitRoll} label={`${a.name} attack`} />}
                        {dmgRoll && <RollChip notation={dmgRoll} label={`${a.name} damage`} />}
                      </div>
                    )}
                    {a.uses > 0 && (
                      <div className="flex items-center gap-2 mt-2.5">
                        <span className="text-[11px] text-zinc-500">Uses</span>
                        <UseCubes max={a.uses} used={a.used || 0} onToggle={(i) => toggleUse(a.id, i)} />
                      </div>
                    )}
                    {a.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{a.notes}</p>}
                  </div>
                  <ConfirmButton onConfirm={() => remove(a.id)} title="Delete action?" message={`Remove "${a.name}" from your actions?`} className="text-zinc-600 hover:text-rose-400 p-1"><Trash2 size={15} /></ConfirmButton>
                </div>
              );
            })}
          </div>
        ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-5 text-center">Add custom actions like special abilities, maneuvers or reactions.</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Add Custom Action">
        <div className="space-y-4">
          <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="action-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type">
              <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                {["Attack", "Action", "Bonus Action", "Reaction", "Feature"].map((k) => <option key={k} className="bg-zinc-900">{k}</option>)}
              </select>
            </Field>
            <Field label="Range"><TextInput value={form.range} onChange={(e) => setForm({ ...form, range: e.target.value })} placeholder="e.g. 60 ft" /></Field>
            <Field label="To-hit / DC"><TextInput value={form.tohit} onChange={(e) => setForm({ ...form, tohit: e.target.value })} placeholder="e.g. +5 or DC 14" /></Field>
            <Field label="Damage"><TextInput value={form.damage} onChange={(e) => setForm({ ...form, damage: e.target.value })} placeholder="e.g. 2d6+3" /></Field>
          </div>
          <Field label="Max uses (0 = unlimited)"><TextInput type="number" min={0} value={form.uses} onChange={(e) => setForm({ ...form, uses: Math.max(0, Number(e.target.value)) })} data-testid="action-uses" /></Field>
          <Field label="Notes"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[80px]" /></Field>
          <Btn className="w-full" onClick={add} data-testid="confirm-add-action">Add Action</Btn>
        </div>
      </Modal>
    </div>
  );
}

function InventoryTab({ char, set }) {
  const [open, setOpen] = useState(false);
  const [item, setItem] = useState({ name: "", quantity: 1, type: "Item", rarity: "Common", description: "" });
  const inv = char.inventory || [];
  const add = () => {
    if (!item.name.trim()) return toast.error("Item name required");
    set({ inventory: [...inv, { ...item, id: crypto.randomUUID() }] });
    setItem({ name: "", quantity: 1, type: "Item", rarity: "Common", description: "" });
    setOpen(false);
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <SectionTitle>Inventory · {inv.length} items</SectionTitle>
        <Btn data-testid="add-item-btn" onClick={() => setOpen(true)}><Plus size={16} /> Add Item</Btn>
      </div>
      {inv.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {inv.map((it) => (
            <div key={it.id} className="glass rounded-2xl p-4 flex items-start gap-3" data-testid={`inv-item-${it.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-white">{it.name}</span>
                  <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: `${RARITY_COLORS[it.rarity]}22`, color: RARITY_COLORS[it.rarity] }}>{it.rarity}</span>
                </div>
                <div className="text-xs text-zinc-500 mt-0.5">{it.type} · x{it.quantity}</div>
                {it.description && <ExpandableText text={it.description} lines={4} className="text-xs text-zinc-400 mt-1.5 leading-relaxed" />}
              </div>
              <ConfirmButton onConfirm={() => set({ inventory: inv.filter((x) => x.id !== it.id) })} title="Delete item?" message={`Remove "${it.name}" from the inventory?`} className="text-zinc-600 hover:text-rose-400 p-1"><Trash2 size={15} /></ConfirmButton>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-8 text-center">Backpack is empty. Add gear from here or the Items library.</p>}
      <Modal open={open} onClose={() => setOpen(false)} title="Add Inventory Item">
        <div className="space-y-4">
          <Field label="Name"><TextInput value={item.name} onChange={(e) => setItem({ ...item, name: e.target.value })} data-testid="item-name" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Qty"><TextInput type="number" min={1} value={item.quantity} onChange={(e) => setItem({ ...item, quantity: Number(e.target.value) })} /></Field>
            <Field label="Type"><TextInput value={item.type} onChange={(e) => setItem({ ...item, type: e.target.value })} /></Field>
            <Field label="Rarity">
              <select value={item.rarity} onChange={(e) => setItem({ ...item, rarity: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
                {Object.keys(RARITY_COLORS).map((r) => <option key={r} className="bg-zinc-900">{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea value={item.description} onChange={(e) => setItem({ ...item, description: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[80px]" /></Field>
          <Btn className="w-full" onClick={add} data-testid="confirm-add-item">Add to Inventory</Btn>
        </div>
      </Modal>
    </div>
  );
}

function SpellsTab({ char, set, caster, onChoose }) {
  const spells = char.spells || [];
  return (
    <div>
      <div className="flex justify-between items-center mb-4 gap-3">
        <SectionTitle>Spells · {spells.length}</SectionTitle>
        {caster ? (
          <Btn data-testid="choose-spells-btn" onClick={onChoose}><Sparkles size={16} /> Choose Spells</Btn>
        ) : <span className="text-xs text-zinc-500 hidden sm:block">Add from the Spells library →</span>}
      </div>
      {caster && <SlotTracker char={char} set={set} />}
      {spells.length ? (
        <div className="space-y-2">
          {[...spells].sort((a, b) => a.level - b.level).map((sp) => (
            <div key={sp.id} className="glass rounded-2xl p-4 flex items-start gap-3" data-testid={`char-spell-${sp.id}`}>
              <div className="w-10 h-10 rounded-xl bg-violet-500/15 border border-violet-500/30 flex items-center justify-center font-display font-bold text-violet-300 shrink-0">{sp.level === 0 ? "C" : sp.level}</div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white">{sp.name}</div>
                <div className="text-xs text-zinc-500">{sp.school} · {sp.level === 0 ? "Cantrip" : `Level ${sp.level}`}</div>
                {sp.description && <ExpandableText text={sp.description} lines={3} className="text-xs text-zinc-400 mt-1.5 leading-relaxed" />}
              </div>
              <ConfirmButton onConfirm={() => set({ spells: spells.filter((x) => x.id !== sp.id) })} title="Remove spell?" message={`Remove "${sp.name}" from this character?`} confirmLabel="Remove" className="text-zinc-600 hover:text-rose-400 p-1"><Trash2 size={15} /></ConfirmButton>
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-8 text-center">{caster ? "Tap \"Choose Spells\" to pick your cantrips and spells." : "No spells yet. Browse the Spells library and tap \"Add to character\"."}</p>}
    </div>
  );
}

function SlotTracker({ char, set }) {
  const slots = spellSlots(char.class, char.level);
  const used = char.spell_slots_used || {};
  const levels = Object.keys(slots).map(Number).sort((a, b) => a - b);
  if (!levels.length) return null;
  const toggle = (lvl, idx) => {
    const u = used[lvl] || 0;
    const next = idx < u ? idx : idx + 1;
    set({ spell_slots_used: { ...used, [lvl]: next } });
  };
  return (
    <div className="glass rounded-2xl p-4 mb-4" data-testid="spell-slots">
      <div className="text-xs uppercase tracking-wide text-zinc-500 mb-3 font-medium">Spell Slots · click a cube to spend</div>
      <div className="flex flex-wrap gap-x-6 gap-y-3">
        {levels.map((lvl) => {
          const total = slots[lvl]; const u = used[lvl] || 0;
          return (
            <div key={lvl} className="flex items-center gap-2">
              <span className="text-xs text-zinc-400 w-12">Lvl {lvl}</span>
              <div className="flex gap-1.5">
                {Array.from({ length: total }).map((_, i) => (
                  <button key={i} onClick={() => toggle(lvl, i)} data-testid={`slot-${lvl}-${i}`}
                    className={`w-5 h-5 rounded-md border transition ${i < u ? "bg-violet-500 border-violet-400" : "border-zinc-600 hover:border-violet-400/60"}`} />
                ))}
              </div>
              <span className="text-[11px] text-zinc-600">{total - u}/{total}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RollChip({ notation, label }) {
  const roll = async (e) => {
    e?.stopPropagation?.();
    try {
      const { data } = await api.post("/dice/roll", { notation, label: label || notation });
      toast.success(`🎲 ${label || notation}: ${data.total}`, { description: `Rolls: [${data.rolls.join(", ")}]${data.modifier ? ` ${data.modifier >= 0 ? "+" : ""}${data.modifier}` : ""}` });
    } catch { toast.error("Invalid dice notation"); }
  };
  return (
    <button onClick={roll} data-testid="roll-chip"
      className="font-mono text-xs px-2.5 py-1 rounded-lg bg-violet-500/15 text-violet-200 border border-violet-500/30 hover:bg-violet-500/25 transition inline-flex items-center gap-1">
      <Dices size={12} /> {notation}
    </button>
  );
}

function UseCubes({ max, used, onToggle }) {
  return (
    <div className="flex items-center gap-1.5">
      {Array.from({ length: max }).map((_, i) => (
        <button key={i} onClick={() => onToggle(i)} data-testid={`use-cube-${i}`}
          className={`w-4 h-4 rounded border transition ${i < used ? "bg-amber-500 border-amber-400" : "border-zinc-600 hover:border-amber-400/60"}`} />
      ))}
    </div>
  );
}

function FeaturesTab({ char, set }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const feats = char.features || [];
  const add = () => {
    if (!name.trim()) return;
    set({ features: [...feats, { id: crypto.randomUUID(), name, description: desc, source: "Custom" }] });
    setName(""); setDesc("");
  };
  return (
    <div className="grid lg:grid-cols-3 gap-5">
      <div className="lg:col-span-2 space-y-3">
        {feats.length ? feats.map((f) => (
          <div key={f.id} className="glass rounded-2xl p-4 flex items-start gap-3" data-testid={`feature-${f.id}`}>
            <Award size={18} className="text-amber-400 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-white">{f.name} {f.source && <span className="text-[10px] text-zinc-600 ml-1">· {f.source}</span>}</div>
              {f.description && <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{f.description}</p>}
            </div>
            <ConfirmButton onConfirm={() => set({ features: feats.filter((x) => x.id !== f.id) })} title="Delete feature?" message={`Remove "${f.name}"?`} className="text-zinc-600 hover:text-rose-400 p-1"><Trash2 size={15} /></ConfirmButton>
          </div>
        )) : <p className="text-sm text-zinc-600 glass rounded-2xl p-8 text-center">Level up to unlock class features, or add your own.</p>}
      </div>
      <div className="glass rounded-2xl p-4 h-fit">
        <SectionTitle>Add Feature</SectionTitle>
        <div className="space-y-3">
          <TextInput placeholder="Feature name" value={name} onChange={(e) => setName(e.target.value)} data-testid="feature-name" />
          <textarea placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[90px]" />
          <Btn className="w-full" onClick={add} data-testid="add-feature-btn"><Plus size={16} /> Add</Btn>
        </div>
      </div>
    </div>
  );
}

function ProficienciesTab({ char, set, reference }) {
  const prof = char.proficiencies || { armor: [], weapons: [], tools: [], languages: [] };
  const klass = (reference?.classes || []).find((c) => c.name === char.class);
  const cats = [["armor", "Armor"], ["weapons", "Weapons"], ["tools", "Tools"], ["languages", "Languages"]];
  const addItem = (cat, val) => { const v = val.trim(); if (!v) return; if ((prof[cat] || []).includes(v)) return; set({ proficiencies: { ...prof, [cat]: [...(prof[cat] || []), v] } }); };
  const removeItem = (cat, idx) => set({ proficiencies: { ...prof, [cat]: prof[cat].filter((_, i) => i !== idx) } });
  const loadFromClass = () => {
    if (!klass) return;
    set({ proficiencies: {
      ...prof,
      armor: Array.from(new Set([...(prof.armor || []), ...(klass.armor || [])])),
      weapons: Array.from(new Set([...(prof.weapons || []), ...(klass.weapons || [])])),
    } });
    toast.success("Loaded class proficiencies");
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <SectionTitle>Proficiencies & Training</SectionTitle>
        {klass && <Btn variant="ghost" data-testid="load-class-prof" onClick={loadFromClass}><ShieldCheck size={15} /> Load from {klass.name}</Btn>}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {cats.map(([key, label]) => (
          <ProfCategory key={key} catKey={key} label={label} items={prof[key] || []} onAdd={(v) => addItem(key, v)} onRemove={(i) => removeItem(key, i)} />
        ))}
      </div>
    </div>
  );
}

function ProfCategory({ catKey, label, items, onAdd, onRemove }) {
  const [val, setVal] = useState("");
  const submit = () => { onAdd(val); setVal(""); };
  return (
    <div className="glass rounded-2xl p-4" data-testid={`prof-cat-${catKey}`}>
      <div className="text-xs uppercase tracking-wide text-zinc-500 mb-3 font-medium">{label}</div>
      <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
        {items.length ? items.map((it, i) => (
          <span key={i} className="text-xs bg-violet-500/15 text-violet-200 border border-violet-500/30 rounded-full pl-3 pr-1.5 py-1 flex items-center gap-1.5">
            {it}
            <button onClick={() => onRemove(i)} className="hover:text-white"><X size={12} /></button>
          </span>
        )) : <span className="text-xs text-zinc-600">None added.</span>}
      </div>
      <div className="flex gap-2">
        <input value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()}
          placeholder={`Add ${label.toLowerCase()}…`} className="input-dark w-full rounded-xl px-3 py-2 text-sm" data-testid={`prof-input-${catKey}`} />
        <Btn variant="ghost" onClick={submit} data-testid={`prof-add-${catKey}`}><Plus size={16} /></Btn>
      </div>
    </div>
  );
}

function BackgroundTab({ char, set, reference }) {
  const bgt = char.bg_traits || { personality: "", ideals: "", bonds: "", flaws: "" };
  const setBgt = (patch) => set({ bg_traits: { ...bgt, ...patch } });
  const backgrounds = reference?.backgrounds || [];
  const alignments = reference?.alignments || [];
  const subOpts = (reference?.classes || []).find((c) => c.name === char.class)?.subclasses || [];
  const fields = [
    ["personality", "Personality Traits"], ["ideals", "Ideals"], ["bonds", "Bonds"], ["flaws", "Flaws"],
  ];
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <div className="glass rounded-2xl p-5 space-y-4">
        <SectionTitle>Background & Details</SectionTitle>
        <Field label="Background">
          <select value={char.background || ""} onChange={(e) => set({ background: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm" data-testid="bg-background">
            <option value="" className="bg-zinc-900">—</option>
            {backgrounds.map((b) => <option key={b.name} className="bg-zinc-900">{b.name}</option>)}
          </select>
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subclass">
            <select value={char.subclass || ""} onChange={(e) => set({ subclass: e.target.value, features: rebuildSubclassFeatures(char, e.target.value, reference) })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm" data-testid="bg-subclass">
              <option value="" className="bg-zinc-900">— None —</option>
              {subOpts.map((s) => <option key={s} className="bg-zinc-900">{s}</option>)}
            </select>
          </Field>
          <Field label="Hit Dice"><TextInput value={char.hit_dice ?? ""} onChange={(e) => set({ hit_dice: e.target.value })} /></Field>
          <Field label="Alignment">
            <select value={char.alignment || ""} onChange={(e) => set({ alignment: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
              {alignments.map((a) => <option key={a} className="bg-zinc-900">{a}</option>)}
            </select>
          </Field>
          <Field label="Experience"><TextInput type="number" value={char.experience ?? 0} onChange={(e) => set({ experience: Number(e.target.value) })} /></Field>
          <Field label="Gold (gp)"><TextInput type="number" value={char.gold ?? 0} onChange={(e) => set({ gold: Number(e.target.value) })} /></Field>
        </div>
      </div>
      <div className="glass rounded-2xl p-5 space-y-4">
        <SectionTitle>Personality</SectionTitle>
        {fields.map(([key, label]) => (
          <Field key={key} label={label}>
            <textarea value={bgt[key] || ""} onChange={(e) => setBgt({ [key]: e.target.value })}
              className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[64px]" data-testid={`bg-${key}`} />
          </Field>
        ))}
      </div>
    </div>
  );
}

function CreaturesTab({ char, set }) {
  const creatures = char.creatures || [];
  const [open, setOpen] = useState(false);
  const blank = { name: "", type: "Beast", ac: 10, hp: { current: 1, max: 1 }, speed: 30, notes: "" };
  const [form, setForm] = useState(blank);
  const add = () => {
    if (!form.name.trim()) return toast.error("Creature name required");
    set({ creatures: [...creatures, { ...form, id: crypto.randomUUID() }] });
    setForm(blank); setOpen(false);
  };
  const adjustHp = (id, delta) => set({ creatures: creatures.map((cr) => cr.id === id ? { ...cr, hp: { ...cr.hp, current: Math.max(0, Math.min(cr.hp.max, (cr.hp.current || 0) + delta)) } } : cr) });
  const remove = (id) => set({ creatures: creatures.filter((cr) => cr.id !== id) });
  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <SectionTitle>Creatures & Custom Creatures · {creatures.length}</SectionTitle>
        <Btn data-testid="add-creature-btn" onClick={() => setOpen(true)}><Plus size={16} /> Add Creature</Btn>
      </div>
      {creatures.length ? (
        <div className="grid sm:grid-cols-2 gap-3">
          {creatures.map((cr) => (
            <div key={cr.id} className="glass rounded-2xl p-4" data-testid={`creature-${cr.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2"><PawPrint size={16} className="text-teal-300" /><span className="font-semibold text-white">{cr.name}</span></div>
                <ConfirmButton onConfirm={() => remove(cr.id)} title="Remove creature?" message={`Delete "${cr.name}" from this character?`} confirmLabel="Remove" className="text-zinc-600 hover:text-rose-400 p-1"><Trash2 size={15} /></ConfirmButton>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">{cr.type} · AC {cr.ac} · {cr.speed} ft</div>
              <div className="flex items-center gap-2 mt-3">
                <span className="text-xs text-zinc-500">HP</span>
                <button onClick={() => adjustHp(cr.id, -1)} className="w-7 h-7 rounded-lg bg-rose-500/15 text-rose-300 hover:bg-rose-500/25 font-bold" data-testid={`creature-hp-minus-${cr.id}`}>−</button>
                <span className="font-display font-bold text-white">{cr.hp?.current}<span className="text-zinc-600">/{cr.hp?.max}</span></span>
                <button onClick={() => adjustHp(cr.id, 1)} className="w-7 h-7 rounded-lg bg-teal-500/15 text-teal-300 hover:bg-teal-500/25 font-bold" data-testid={`creature-hp-plus-${cr.id}`}>+</button>
              </div>
              {cr.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{cr.notes}</p>}
            </div>
          ))}
        </div>
      ) : <p className="text-sm text-zinc-600 glass rounded-2xl p-8 text-center">Track companions, summons, familiars or custom creatures here.</p>}
      <Modal open={open} onClose={() => setOpen(false)} title="Add Creature">
        <div className="space-y-4">
          <Field label="Name"><TextInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="creature-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Type"><TextInput value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} placeholder="e.g. Beast, Fey" /></Field>
            <Field label="Speed (ft)"><TextInput type="number" value={form.speed} onChange={(e) => setForm({ ...form, speed: Number(e.target.value) })} /></Field>
            <Field label="Armor Class"><TextInput type="number" value={form.ac} onChange={(e) => setForm({ ...form, ac: Number(e.target.value) })} /></Field>
            <Field label="Max HP"><TextInput type="number" value={form.hp.max} onChange={(e) => setForm({ ...form, hp: { current: Number(e.target.value), max: Number(e.target.value) } })} /></Field>
          </div>
          <Field label="Notes / Attacks"><textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm min-h-[80px]" /></Field>
          <Btn className="w-full" onClick={add} data-testid="confirm-add-creature">Add Creature</Btn>
        </div>
      </Modal>
    </div>
  );
}

function EditModal({ open, onClose, char, set, reference }) {
  const [form, setForm] = useState(char);
  const [method, setMethod] = useState("manual");
  useEffect(() => { if (open) { setForm(char); setMethod(char.ability_method || "manual"); } }, [open, char]);
  const races = reference?.races || [];
  const classes = reference?.classes || [];
  const subOpts = (classes.find((c) => c.name === form.class)?.subclasses) || [];
  const ab = form.abilities || {};
  const pbUsed = pointBuyTotal(ab);

  const adjust = (k, d) => {
    const v = (ab[k] || 8) + d;
    if (method === "pointbuy") {
      if (v < 8 || v > 15) return;
      const next = { ...ab, [k]: v };
      if (pointBuyTotal(next) > POINT_BUY_BUDGET) return;
      setForm({ ...form, abilities: next });
    } else {
      if (v < 1 || v > 30) return;
      setForm({ ...form, abilities: { ...ab, [k]: v } });
    }
  };

  const save = () => {
    const newLevel = Math.max(1, Math.min(20, Number(form.level) || 1));
    const klass = classes.find((c) => c.name === form.class);
    const subOptions = klass?.subclasses || [];
    const subclass = subOptions.includes(form.subclass) ? form.subclass : (form.subclass && !subOptions.length ? form.subclass : "");
    const patch = {
      name: form.name, race: form.race, class: form.class, subclass, background: form.background,
      alignment: form.alignment, level: newLevel,
      abilities: form.abilities, proficiency_bonus: profBonusForLevel(newLevel), ability_method: method,
    };
    // Recompute max HP deterministically whenever the level changes so that
    // leveling DOWN restores the correct lower maximum (bug fix) and leveling
    // up stays consistent with the Level Up button.
    if (newLevel !== char.level) {
      const hitDie = klass?.hit_die || 8;
      const conMod = mod(form.abilities?.constitution);
      const newMax = maxHpForLevel(hitDie, newLevel, conMod);
      const curHp = char.hp || { current: newMax, max: newMax, temp: 0 };
      patch.hp = { ...curHp, max: newMax, current: Math.min(curHp.current ?? newMax, newMax) };
      patch.hit_dice = `${newLevel}d${hitDie}`;
    }
    if (subclass !== (char.subclass || "")) {
      patch.features = rebuildSubclassFeatures({ ...char, class: form.class, level: newLevel }, subclass, reference);
    }
    set(patch);
    onClose();
    toast.success("Character updated");
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit Character" size="lg">
      <div className="space-y-4">
        <Field label="Name"><TextInput value={form.name || ""} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="edit-name" /></Field>
        <div className="grid grid-cols-2 gap-4">
          <Field label="Race">
            <select value={form.race} onChange={(e) => setForm({ ...form, race: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
              {races.map((r) => <option key={r.name} className="bg-zinc-900">{r.name}</option>)}
            </select>
          </Field>
          <Field label="Class">
            <select value={form.class} onChange={(e) => setForm({ ...form, class: e.target.value, subclass: "" })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
              {classes.map((c) => <option key={c.name} className="bg-zinc-900">{c.name}</option>)}
            </select>
          </Field>
          <Field label="Subclass">
            <select value={form.subclass || ""} onChange={(e) => setForm({ ...form, subclass: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm" data-testid="edit-subclass">
              <option value="" className="bg-zinc-900">— None —</option>
              {subOpts.map((s) => <option key={s} className="bg-zinc-900">{s}</option>)}
            </select>
          </Field>
          <Field label="Level"><TextInput type="number" min={1} max={20} value={form.level} onChange={(e) => setForm({ ...form, level: Number(e.target.value) })} data-testid="edit-level" /></Field>
          <Field label="Alignment">
            <select value={form.alignment} onChange={(e) => setForm({ ...form, alignment: e.target.value })} className="input-dark w-full rounded-xl px-3 py-2.5 text-sm">
              {(reference?.alignments || []).map((a) => <option key={a} className="bg-zinc-900">{a}</option>)}
            </select>
          </Field>
        </div>

        <div className="flex items-center justify-between">
          <SectionTitle>Ability Scores</SectionTitle>
          <div className="flex gap-1.5">
            {[["manual", "Manual"], ["pointbuy", "Point Buy"]].map(([m, l]) => (
              <button key={m} onClick={() => setMethod(m)} className={`px-3 py-1.5 rounded-lg text-xs font-medium ${method === m ? "btn-violet" : "glass text-zinc-400"}`}>{l}</button>
            ))}
          </div>
        </div>
        {method === "pointbuy" && <div className="text-xs text-violet-300">Points used {pbUsed}/{POINT_BUY_BUDGET}</div>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {ABILITIES.map((a) => (
            <div key={a.key} className="glass rounded-xl p-2.5 flex items-center gap-2" data-testid={`edit-ability-${a.key}`}>
              <div className="flex-1">
                <div className="text-[10px] text-zinc-500">{a.label}</div>
                <div className="text-xs text-violet-300">{fmtMod(mod(ab[a.key]))}</div>
              </div>
              <button onClick={() => adjust(a.key, -1)} className="w-6 h-6 rounded bg-white/[0.06] hover:bg-white/[0.12]">−</button>
              <span className="font-display text-lg font-bold w-7 text-center">{ab[a.key]}</span>
              <button onClick={() => adjust(a.key, 1)} className="w-6 h-6 rounded bg-white/[0.06] hover:bg-white/[0.12]">+</button>
            </div>
          ))}
        </div>

        <div className="flex gap-3 pt-2">
          <Btn variant="ghost" className="flex-1" onClick={onClose}>Cancel</Btn>
          <Btn className="flex-1" onClick={save} data-testid="edit-save">Save Changes</Btn>
        </div>
      </div>
    </Modal>
  );
}

function SectionTitle({ children }) {
  return <h3 className="font-display text-sm uppercase tracking-wider text-zinc-400 mb-3">{children}</h3>;
}
